import {
  DISMISS_POLICY,
  GUARDED_REASONS,
  TRANSIENT_KINDS,
  type LayerCloseReason,
  type LayerEntry,
  type LayerKind,
  type LayerNavigationEvent,
  type LayerOptions,
} from './types';

/** Ids of the two portal containers LayerRoot owns (plan §4.1). */
export const LAYERS_CONTAINER_ID = 'dr-layers';
export const SYSTEM_CONTAINER_ID = 'dr-system';
/** Attribute set on every layer's portal element; DOM order inside `#dr-layers` = stacking order. */
export const LAYER_ID_ATTR = 'data-layer-id';
/** Attribute set on suspended layers (an ancestor popover while its modal child is open). */
export const SUSPENDED_ATTR = 'data-layer-suspended';
/** Attribute set on modal layers covered by a higher modal layer: their scrim is hidden (one scrim). */
export const COVERED_ATTR = 'data-layer-covered';

type Internal = {
  id: string;
  kind: LayerKind;
  order: number;
  parentId: string | null;
  opts: { current: LayerOptions };
  suspendedBy: string | null;
  /** Element focused when the layer opened (trigger), its ancestors, and the layer that held it. */
  opener: Element | null;
  openerChain: Element[];
  openerLayerId: string | null;
  closing: boolean;
  meta: { parentId: string | null; opener: Element | null };
};

export type ConfirmRequest = {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** The user must type this text (the name of what is deleted) to enable the confirm button. */
  typeToConfirm?: string;
};

type ConfirmHost = (request: ConfirmRequest) => Promise<boolean>;

/** Dismiss decision for a Base UI primitive (`handleDismiss`). */
export type DismissVerdict =
  /** Close now. */
  | 'close'
  /** Do not close; let the event propagate (the layer is not on top, the stack routes it). */
  | 'ignore'
  /** Do not close; the dirty guard is asking. */
  | 'guarded';

const isElement = (x: unknown): x is Element => typeof Element !== 'undefined' && x instanceof Element;

function focusable(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement) || !el.isConnected) return false;
  if (el.closest('[inert]')) return false;
  if ((el as HTMLButtonElement).disabled) return false;
  if (el.tabIndex >= 0) return true;
  return el.hasAttribute('tabindex') || el.isContentEditable;
}

/**
 * One stack per document. Pure TypeScript (no React): primitives register through `useLayer`, the
 * global listeners and LayerRoot read it. DOM order inside `#dr-layers` mirrors registration order
 * because every layer portals into the container when it opens.
 */
export class LayerStack {
  private entries: Internal[] = [];
  private listeners = new Set<() => void>();
  private seq = 0;
  private orderSeq = 0;
  private inerted = new Set<Element>();
  private covered = new Set<Element>();
  private confirmHost: ConfirmHost | null = null;
  private globalCleanup: (() => void) | null = null;
  private dirtySources = new Set<(layerId: string) => boolean>();

  constructor(private readonly doc: Document | null = typeof document === 'undefined' ? null : document) {}

  // -- Registration -----------------------------------------------------------------------------

  nextId(): string {
    this.seq += 1;
    return `layer-${this.seq}`;
  }

  register(
    id: string,
    opts: { current: LayerOptions },
    meta: { parentId: string | null; opener: Element | null },
  ): void {
    if (this.entries.some((e) => e.id === id)) return;
    this.ensureGlobalListeners();
    this.orderSeq += 1;
    const opener = meta.opener && meta.opener !== this.doc?.body ? meta.opener : null;
    const openerChain: Element[] = [];
    for (let el = opener?.parentElement ?? null; el; el = el.parentElement) openerChain.push(el);
    const entry: Internal = {
      id,
      kind: opts.current.kind,
      order: this.orderSeq,
      parentId: meta.parentId,
      opts,
      suspendedBy: null,
      opener,
      openerChain,
      openerLayerId: opener ? (this.layerContaining(opener)?.id ?? null) : null,
      closing: false,
      meta,
    };
    this.entries.push(entry);
    if (this.isModal(entry)) this.onModalOpened(entry);
    this.changed();
  }

  unregister(id: string): void {
    const index = this.entries.findIndex((e) => e.id === id);
    if (index < 0) return;
    const [entry] = this.entries.splice(index, 1);
    // Ancestors suspended by this modal child close with it (they were hidden meanwhile).
    for (const other of [...this.entries].reverse())
      if (other.suspendedBy === id) {
        other.suspendedBy = null;
        this.closeEntry(other, 'child-closed');
      }
    this.changed();
    if (entry) this.restoreFocus(entry);
  }

  /** Re-read options (dirty, elements) after a render; cheap, no reorder. */
  touch(): void {
    this.changed();
  }

  // -- Queries ----------------------------------------------------------------------------------

  list(): LayerEntry[] {
    return this.entries.map((e) => ({
      id: e.id,
      kind: e.kind,
      modal: this.isModal(e),
      pinned: Boolean(e.opts.current.pinned),
      parentId: e.parentId,
      order: e.order,
      suspended: e.suspendedBy !== null,
      dirty: this.isDirty(e),
      label: e.opts.current.label,
    }));
  }

  /** Top-most layer that is not suspended. */
  top(): LayerEntry | undefined {
    const e = this.topInternal();
    return e ? this.list().find((x) => x.id === e.id) : undefined;
  }

  isTop(id: string): boolean {
    return this.topInternal()?.id === id;
  }

  has(id: string): boolean {
    return this.entries.some((e) => e.id === id);
  }

  anyDirty(): boolean {
    return this.entries.some((e) => this.isDirty(e) && !e.opts.current.pinned);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Another source of unsaved work per layer (interaction-kit's `useDirtyScope` inside a dialog or
   * drawer): a layer is dirty when its own `dirty` option or any source says so.
   */
  addDirtySource(source: (layerId: string) => boolean): () => void {
    this.dirtySources.add(source);
    return () => this.dirtySources.delete(source);
  }

  private isDirty(entry: Internal): boolean {
    if (entry.opts.current.dirty) return true;
    for (const source of this.dirtySources) if (source(entry.id)) return true;
    return false;
  }

  // -- Closing ----------------------------------------------------------------------------------

  /**
   * Ask a layer to close. Guarded reasons consult the dirty guard first. Resolves true when the
   * layer closed (or was already gone).
   */
  async requestClose(id: string, reason: LayerCloseReason): Promise<boolean> {
    const entry = this.entries.find((e) => e.id === id);
    if (!entry) return true;
    if (this.isDirty(entry) && GUARDED_REASONS.has(reason)) {
      const discard = await this.askDiscard(entry);
      if (!discard) return false;
    }
    this.closeEntry(entry, reason);
    return true;
  }

  /**
   * Dismiss decision for primitives that detect Escape / outside press / focus-out themselves (Base UI).
   * `close` means the caller closes now; `guarded` means the guard is asking (and will close on
   * "Discard"); `ignore` means the event belongs to another layer.
   */
  handleDismiss(id: string, reason: LayerCloseReason, event?: Event): DismissVerdict {
    const entry = this.entries.find((e) => e.id === id);
    if (!entry) return 'close';
    if (reason === 'escape') {
      if (!this.isTop(id)) return 'ignore';
    } else if (reason === 'outside') {
      const target = event?.target;
      if (isElement(target) && this.isInsideLayerAbove(entry, target)) return 'ignore';
      if (entry.suspendedBy) return 'ignore';
    } else if (reason === 'focus-out') {
      const next = (event as FocusEvent | undefined)?.relatedTarget;
      if (isElement(next) && this.isInsideLayerAbove(entry, next)) return 'ignore';
      if (entry.suspendedBy) return 'ignore';
    }
    if (this.isDirty(entry) && GUARDED_REASONS.has(reason)) {
      void this.askDiscard(entry).then((discard) => {
        if (discard) this.closeEntry(entry, reason);
      });
      return 'guarded';
    }
    entry.closing = true;
    // Remove eagerly so `inert` is lifted before the primitive returns focus to its trigger.
    this.unregister(id);
    this.reconcileLater(entry);
    return 'close';
  }

  /**
   * Called before a navigation is allowed (router blocker). Resolves true when the navigation may
   * proceed: every dirty layer that the navigation would close was confirmed ("Discard").
   */
  async confirmNavigation(event: LayerNavigationEvent): Promise<boolean> {
    if (!this.closesOnNavigation(event)) return true;
    // Let synchronous closes of the same handler commit first (a create dialog that navigates to the
    // new record closes itself in the same click).
    await new Promise((resolve) => setTimeout(resolve, 0));
    const dirty = this.entries.filter((e) => this.navigationCloses(e) && this.isDirty(e)).reverse();
    for (const entry of dirty) {
      if (!this.entries.includes(entry)) continue;
      // Layers above the dirty one (a palette that navigates) close first so the guard is visible.
      for (const above of [...this.entries].reverse()) {
        if (above.order <= entry.order) break;
        if (!this.isDirty(above) && this.navigationCloses(above)) this.closeEntry(above, 'navigate');
      }
      const discard = await this.askDiscard(entry);
      if (!discard) return false;
      this.closeEntry(entry, 'navigate');
    }
    return true;
  }

  /** Close every layer the navigation closes (pathname change or history traversal). */
  closeForNavigation(event: LayerNavigationEvent): void {
    if (!this.closesOnNavigation(event)) return;
    for (const entry of [...this.entries].reverse())
      if (this.navigationCloses(entry)) this.closeEntry(entry, 'navigate');
  }

  /** `beforeunload` guard: true while a dirty layer would be lost. */
  shouldBlockUnload(): boolean {
    return this.anyDirty();
  }

  /** Close every non-pinned layer (tests, sign-out). */
  closeAll(reason: LayerCloseReason = 'programmatic'): void {
    for (const entry of [...this.entries].reverse())
      if (!entry.opts.current.pinned) this.closeEntry(entry, reason);
  }

  // -- Confirm host -----------------------------------------------------------------------------

  setConfirmHost(host: ConfirmHost | null): void {
    this.confirmHost = host;
  }

  /** Promise-based confirm rendered by LayerRoot (`useConfirm`). Resolves false without a host. */
  confirm(request: ConfirmRequest): Promise<boolean> {
    return this.confirmHost ? this.confirmHost(request) : Promise.resolve(false);
  }

  // -- Containers -------------------------------------------------------------------------------

  /** `#dr-layers` (z `--z-layers`) and `#dr-system` (z `--z-system`, toasts), created on demand. */
  container(kind: 'layers' | 'system' = 'layers'): HTMLElement | null {
    const doc = this.doc;
    if (!doc?.body) return null;
    const id = kind === 'layers' ? LAYERS_CONTAINER_ID : SYSTEM_CONTAINER_ID;
    let el = doc.getElementById(id);
    if (!el) {
      el = doc.createElement('div');
      el.id = id;
      el.className = kind === 'layers' ? 'dr-layers' : 'dr-system';
      // Modal primitives hide everything outside themselves from assistive tech except live regions.
      // Toasts mount here lazily (often while a dialog is open): marking the container keeps them
      // readable; `off` leaves the announcing to the toast region inside.
      if (kind === 'system') el.setAttribute('aria-live', 'off');
      if (kind === 'layers') {
        // The system container (toasts) must stay after the layers container in DOM order.
        const system = doc.getElementById(SYSTEM_CONTAINER_ID);
        if (system) doc.body.insertBefore(el, system);
        else doc.body.appendChild(el);
      } else doc.body.appendChild(el);
    }
    return el;
  }

  // -- Global listeners: one Escape router, outside presses of router layers ---------------------

  ensureGlobalListeners(): void {
    const doc = this.doc;
    const win = doc?.defaultView;
    if (this.globalCleanup || !doc || !win) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      if (event.key !== 'Escape') return;
      const top = this.topInternal();
      if (!top || top.opts.current.escape === 'self') return;
      event.preventDefault();
      // A layer may spend Escape on its own first step (the Copilot stops a streaming run; the
      // second Escape closes it).
      if (top.opts.current.onEscape?.(event) === true) return;
      void this.requestClose(top.id, 'escape');
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!isElement(target)) return;
      for (const entry of [...this.entries].reverse()) {
        const o = entry.opts.current;
        // The kind's policy decides by default: a docked panel (Copilot) or an alert never closes
        // because the member clicked the page beside it.
        const outside = o.dismissOnOutsidePress ?? DISMISS_POLICY[entry.kind].outside;
        if (o.escape === 'self' || !outside || entry.suspendedBy) continue;
        if (this.isInside(entry, target) || this.isInsideLayerAbove(entry, target)) continue;
        void this.requestClose(entry.id, 'outside');
      }
    };
    // Bubble phase: an inner handler that claims Escape (preventDefault / stopPropagation) wins.
    win.addEventListener('keydown', onKeyDown);
    win.addEventListener('pointerdown', onPointerDown, true);
    this.globalCleanup = () => {
      win.removeEventListener('keydown', onKeyDown);
      win.removeEventListener('pointerdown', onPointerDown, true);
    };
  }

  dispose(): void {
    this.globalCleanup?.();
    this.globalCleanup = null;
    this.entries = [];
    this.applyDom();
  }

  // -- Internals --------------------------------------------------------------------------------

  private topInternal(): Internal | undefined {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const e = this.entries[i]!;
      if (!e.suspendedBy) return e;
    }
    return undefined;
  }

  private isModal(entry: Internal): boolean {
    return entry.opts.current.modal ?? DISMISS_POLICY[entry.kind].modal;
  }

  private navigationCloses(entry: Internal): boolean {
    return !entry.opts.current.pinned && DISMISS_POLICY[entry.kind].route !== 'keep';
  }

  private closesOnNavigation(event: LayerNavigationEvent): boolean {
    return event.kind === 'traverse' || event.pathChanged;
  }

  private elementsOf(entry: Internal): Element[] {
    const out: Element[] = [];
    const doc = this.doc;
    if (doc) out.push(...Array.from(doc.querySelectorAll(`[${LAYER_ID_ATTR}="${entry.id}"]`)));
    for (const el of entry.opts.current.getElements?.() ?? []) if (isElement(el)) out.push(el);
    return out;
  }

  private isInside(entry: Internal, target: Element): boolean {
    return this.elementsOf(entry).some((el) => el.contains(target));
  }

  private isInsideLayerAbove(entry: Internal, target: Element): boolean {
    if (target.closest(`#${SYSTEM_CONTAINER_ID}`)) return true;
    return this.entries.some((other) => other.order > entry.order && this.isInside(other, target));
  }

  private layerContaining(target: Element): Internal | undefined {
    for (const entry of [...this.entries].reverse()) if (this.isInside(entry, target)) return entry;
    return undefined;
  }

  private isAncestor(maybeAncestor: Internal, child: Internal): boolean {
    let parent = child.parentId;
    const seen = new Set<string>();
    while (parent && !seen.has(parent)) {
      if (parent === maybeAncestor.id) return true;
      seen.add(parent);
      parent = this.entries.find((e) => e.id === parent)?.parentId ?? null;
    }
    return false;
  }

  /** Opening a modal closes transient layers, or suspends them when they are its ancestors. */
  private onModalOpened(modal: Internal): void {
    for (const entry of [...this.entries].reverse()) {
      if (entry === modal || !TRANSIENT_KINDS.has(entry.kind) || entry.suspendedBy) continue;
      if (this.isAncestor(entry, modal)) entry.suspendedBy = modal.id;
      else this.closeEntry(entry, 'superseded');
    }
  }

  private closeEntry(entry: Internal, reason: LayerCloseReason): void {
    if (!this.entries.includes(entry)) return;
    // Children (registered later with this layer as ancestor) close first.
    for (const child of [...this.entries].reverse())
      if (child !== entry && this.isAncestor(entry, child)) this.closeEntry(child, 'programmatic');
    entry.closing = true;
    // Remove eagerly so inert is lifted before the primitive returns focus to its trigger.
    this.unregister(entry.id);
    entry.opts.current.onClose(reason);
    this.reconcileLater(entry);
  }

  /**
   * A layer removed eagerly whose owner refused to close (an `onClose` that ignores the request while
   * saving) is registered again, so Escape routing and `inert` stay correct.
   */
  private reconcileLater(entry: Internal): void {
    setTimeout(() => {
      if (entry.opts.current.open && !this.has(entry.id)) {
        entry.closing = false;
        this.register(entry.id, entry.opts, entry.meta);
      }
    }, 0);
  }

  private async askDiscard(entry: Internal): Promise<boolean> {
    const own = entry.opts.current.confirmDiscard;
    if (own) return own();
    return this.confirm({ title: '', body: entry.opts.current.label, danger: true });
  }

  private changed(): void {
    this.applyDom();
    for (const l of [...this.listeners]) l();
  }

  /** `inert` on everything below the top modal layer; hide scrims of covered modal layers. */
  private applyDom(): void {
    const doc = this.doc;
    if (!doc?.body) return;
    let topModal = -1;
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const e = this.entries[i]!;
      if (!e.suspendedBy && this.isModal(e)) {
        topModal = i;
        break;
      }
    }
    const inert = new Set<Element>();
    const covered = new Set<Element>();
    if (topModal >= 0) {
      for (const child of Array.from(doc.body.children)) {
        if (child.id === LAYERS_CONTAINER_ID || child.id === SYSTEM_CONTAINER_ID) continue;
        if (child.tagName === 'SCRIPT' || child.hasAttribute('aria-live')) continue;
        // Elements of open layers rendered outside the containers (legacy portals) stay live.
        if (this.entries.slice(topModal).some((e) => this.elementsOf(e).some((el) => child.contains(el))))
          continue;
        inert.add(child);
      }
      for (let i = 0; i < topModal; i++) {
        const e = this.entries[i]!;
        for (const portal of Array.from(doc.querySelectorAll(`[${LAYER_ID_ATTR}="${e.id}"]`))) {
          inert.add(portal);
          if (this.isModal(e)) covered.add(portal);
        }
      }
    }
    for (const el of this.inerted) if (!inert.has(el)) el.removeAttribute('inert');
    for (const el of inert) if (!el.hasAttribute('inert')) el.setAttribute('inert', '');
    this.inerted = inert;
    for (const el of this.covered) if (!covered.has(el)) el.removeAttribute(COVERED_ATTR);
    for (const el of covered) el.setAttribute(COVERED_ATTR, '');
    this.covered = covered;
    for (const e of this.entries)
      for (const portal of Array.from(doc.querySelectorAll(`[${LAYER_ID_ATTR}="${e.id}"]`))) {
        if (e.suspendedBy) portal.setAttribute(SUSPENDED_ATTR, '');
        else portal.removeAttribute(SUSPENDED_ATTR);
      }
  }

  /**
   * Focus return when the primitive could not (conditionally mounted dialogs, removed menu items):
   * the opener, else the opener of the layer that held it, else the nearest focusable ancestor.
   */
  private restoreFocus(entry: Internal): void {
    const doc = this.doc;
    if (!doc || !entry.opener) return;
    const attempt = () => {
      const active = doc.activeElement;
      if (active && active !== doc.body && active.isConnected) return;
      // With a modal layer still open, focus may only return into it.
      const modal = [...this.entries].reverse().find((e) => this.isModal(e) && !e.suspendedBy);
      const targets: Element[] = [];
      if (entry.opener) targets.push(entry.opener);
      let holder = entry.openerLayerId;
      const seen = new Set<string>();
      while (holder && !seen.has(holder)) {
        seen.add(holder);
        const info = this.closedOpeners.get(holder);
        if (!info) break;
        if (info.opener) targets.push(info.opener);
        holder = info.openerLayerId;
      }
      // A trigger re-rendered meanwhile (React replaced the element): the same control in its place.
      const twin =
        entry.opener && !entry.opener.isConnected ? findTwin(entry.opener, entry.openerChain) : null;
      if (twin) targets.unshift(twin);
      targets.push(...entry.openerChain);
      const allowed = (el: Element) => !modal || this.isInside(modal, el);
      for (const el of targets)
        if (focusable(el) && allowed(el)) {
          el.focus({ preventScroll: true });
          return;
        }
      // No focusable ancestor: focus the nearest connected container so keyboard users keep their place.
      const container = entry.openerChain.find(
        (el) => el.isConnected && el !== doc.body && el !== doc.documentElement && allowed(el),
      );
      if (container instanceof HTMLElement) {
        if (!container.hasAttribute('tabindex')) {
          container.setAttribute('tabindex', '-1');
          container.setAttribute('data-focus-return', '');
          container.addEventListener(
            'blur',
            () => {
              container.removeAttribute('tabindex');
              container.removeAttribute('data-focus-return');
            },
            { once: true },
          );
        }
        container.focus({ preventScroll: true });
      }
    };
    this.closedOpeners.set(entry.id, { opener: entry.opener, openerLayerId: entry.openerLayerId });
    if (this.closedOpeners.size > 50) {
      const first = this.closedOpeners.keys().next().value;
      if (first) this.closedOpeners.delete(first);
    }
    setTimeout(attempt, 0);
    // Base UI returns focus after its exit animation; check again once it settled.
    setTimeout(attempt, 260);
  }

  private closedOpeners = new Map<string, { opener: Element | null; openerLayerId: string | null }>();
}

/** The element that replaced a removed trigger: same tag, role, name and text under its old parent. */
export function findTwin(opener: Element, chain: Element[]): Element | null {
  const parent = chain.find((el) => el.isConnected);
  if (!parent) return null;
  const key = (el: Element) =>
    `${el.tagName}|${el.getAttribute('role') ?? ''}|${el.getAttribute('aria-label') ?? ''}|${(el.textContent ?? '').trim()}`;
  const wanted = key(opener);
  for (const el of Array.from(parent.querySelectorAll(opener.tagName))) if (key(el) === wanted) return el;
  return null;
}

let defaultStack: LayerStack | null = null;

/** The document's stack (one per page). */
export function getLayerStack(): LayerStack {
  if (!defaultStack) defaultStack = new LayerStack();
  return defaultStack;
}

/** Tests: drop the singleton (and its listeners). */
export function resetLayerStack(): void {
  defaultStack?.dispose();
  defaultStack = null;
}
